import TextField from 'Containers/Common/TextField';
import { useTranslation } from 'react-i18next';
import styles from './index.module.scss';
import { Formik, Form, Field } from 'formik';
import * as yup from 'yup';
import { useDispatch } from 'react-redux';
import { authActions } from 'Containers/Auth/store';
import clsx from 'clsx';
import useWindowSize from 'utils/useWindowSize';
import { CircleCheck, KeyRound } from 'lucide-react';
import LogoImage from '../../../../assets/images/logo.png';
import { useEffect, useState } from 'react';
import { ROUTES } from 'routes/constants';
import { useNavigate } from 'react-router-dom';
import LanguageSelector from '../../../Common/LanguageSelector';

const fieldProps = {
  component: TextField,
  height: '50px',
  margin: '0 0 16px 0',
  type: 'password',
  showPassword: true,
  showIcon: true,
};

const ResetPwdPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isMobile } = useWindowSize();
  const [reset, setReset] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const salonName = localStorage.getItem('name') || '';

  const validationSchema = {
    newPwd: yup.string().min(3).required(t('auth.validation.required')),
    newPwdConfirm: yup
      .string()
      .required(t('auth.validation.required'))
      .oneOf([yup.ref('newPwd'), null], t('auth.validation.passwordsMustMatch')),
  };

  const handleClick = (value) => {
    if (isSaving) return;
    setIsSaving(true);
    const safetyTimer = window.setTimeout(() => setIsSaving(false), 20000);
    dispatch(
      authActions.RESET_PASSWORD.REQUEST({ newPwd: value }, () => {
        window.clearTimeout(safetyTimer);
        setIsSaving(false);
        setReset(true);
      }),
    );
  };

  useEffect(() => {
    if (!localStorage.getItem('tokenRecovery') && !reset) navigate(ROUTES.SIGN_IN);
  }, []);

  return (
    <div className={clsx(styles.container, { [styles.container_mbl]: isMobile })}>
      <div className={styles.languageSelector}>
        <LanguageSelector />
      </div>
      <div className={styles.card}>
        <img src={LogoImage} alt={t('common.logo')} className={styles.logo} />

        {!reset ? (
          <>
            <span className={styles.card_icon} aria-hidden="true">
              <KeyRound size={18} strokeWidth={2.2} />
            </span>
            <h1>{t('auth.resetPwdPage.resetPassword')}</h1>
            <p className={styles.lead}>
              {salonName
                ? t('auth.resetPwdPage.resettingForSalon', { salon: salonName })
                : t('auth.resetPwdPage.enterNewPassword')}
            </p>
            <Formik
              initialValues={{ newPwd: '', newPwdConfirm: '' }}
              validationSchema={yup.object().shape(validationSchema)}
              onSubmit={(values) => handleClick(values.newPwd)}
            >
              <Form className={styles.form}>
                <Field
                  {...fieldProps}
                  name="newPwd"
                  id="newPwd"
                  autoComplete="new-password"
                  label={t('auth.resetPwdPage.newPassword')}
                  placeholder={t('auth.resetPwdPage.newPassword')}
                />
                <Field
                  {...fieldProps}
                  name="newPwdConfirm"
                  id="newPwdConfirm"
                  autoComplete="new-password"
                  label={t('auth.resetPwdPage.confirmPassword')}
                  placeholder={t('auth.resetPwdPage.confirmPassword')}
                />
                <button type="submit" className={styles.confirm_button} disabled={isSaving}>
                  {isSaving ? t('auth.resetPwdPage.saving') : t('auth.resetPwdPage.save')}
                </button>
              </Form>
            </Formik>
            <button
              type="button"
              className={styles.back_link}
              onClick={() => navigate(ROUTES.SIGN_IN)}
            >
              {t('auth.resetPwdPage.backToLogin')}
            </button>
          </>
        ) : (
          <>
            <span className={clsx(styles.card_icon, styles.card_icon_success)} aria-hidden="true">
              <CircleCheck size={18} strokeWidth={2.2} />
            </span>
            <h1>{t('auth.resetPwdPage.completed')}</h1>
            <p className={styles.lead}>{t('auth.resetPwdPage.passwordChanged')}</p>
            <p className={styles.hint}>{t('auth.resetPwdPage.loginWithNewPassword')}</p>
            <button
              type="button"
              className={styles.confirm_button}
              onClick={() => navigate(ROUTES.SIGN_IN)}
            >
              {t('auth.resetPwdPage.goToLogin')}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default ResetPwdPage;
